import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';

import {
  EMPTY,
  Observable,
  catchError,
  exhaustMap,
  finalize,
  switchMap,
  tap,
} from 'rxjs';

import {
  ActivateAuthenticatorModel,
  ActivateAuthenticatorRequestModel,
  AuthenticatorCodeModel,
  AuthenticatorCodeRequestModel,
  AuthenticatorKeyModel,
  ForgotPasswordRequestModel,
  PermissionModel,
  ResetPasswordModel,
  ResetPasswordRequestModel,
  SignInModel,
  SignInRequestModel,
  SignOutModel,
  TokenModel,
  VerifyAuthenticatorModel,
  VerifyAuthenticatorRequestModel,
  VerifyModel,
} from '../../models/identity/authentication.model';

import { AuthenticationFetchService } from '../../fetches/services/authentication-fetch.service';

import { ResultState } from '../common/states/result.state';

export interface AuthenticationState {
  signIn: ResultState<SignInModel> | null;
  authenticatorKey: ResultState<AuthenticatorKeyModel> | null;
  authenticatorCode: ResultState<AuthenticatorCodeModel> | null;
  verify: ResultState<VerifyModel> | null;
  token: ResultState<TokenModel> | null;
  permission: ResultState<PermissionModel[]> | null;
  forgotPassword: ResultState<ResetPasswordModel> | null;
  resetPassword: ResultState<ResetPasswordModel> | null;
  verifyAuthenticator: ResultState<VerifyAuthenticatorModel> | null;
  activateAuthenticator: ResultState<ActivateAuthenticatorModel> | null;
  signOut: ResultState<SignOutModel> | null;
}

const initialState: AuthenticationState = {
  signIn: null,
  authenticatorKey: null,
  authenticatorCode: null,
  verify: null,
  token: null,
  permission: null,
  forgotPassword: null,
  resetPassword: null,
  verifyAuthenticator: null,
  activateAuthenticator: null,
  signOut: null,
};

@Injectable()
export class AuthenticationStore extends ComponentStore<AuthenticationState> {
  constructor(private _authenticationFetchService: AuthenticationFetchService) {
    super(initialState);
  }

  public readonly signIn = this.effect(
    (request$: Observable<SignInRequestModel>) => {
      return request$.pipe(
        tap(() =>
          this.patchState((value) => ({
            ...value,
            signIn: {
              event: {
                processing: true,
              },
            },
          }))
        ),
        switchMap((request) =>
          this._authenticationFetchService.signIn(request).pipe(
            tap({
              next: (response) => {
                if (response && response.status) {
                  this.patchState((value) => ({
                    ...value,
                    signIn: {
                      ...value.signIn,
                      payload: response.payload,
                    },
                  }));
                } else {
                  this.patchState((value) => ({
                    ...value,
                    signIn: {
                      ...value.signIn,
                      error: {
                        code: response.code,
                        message: response.message,
                        translate: 'ERROR.EMAIL_OR_PASSWORD_INCORRECT',
                      },
                    },
                  }));
                }
              },
              error: console.error,
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this.patchState((value) => ({
                ...value,
                signIn: {
                  ...value.signIn,
                  event: {
                    processing: false,
                  },
                },
              }))
            )
          )
        )
      );
    }
  );

  public readonly authenticatorKey = this.effect(($) =>
    $.pipe(
      tap(() =>
        this.patchState((value) => ({
          ...value,
          authenticatorKey: {
            event: {
              loading: true,
            },
          },
        }))
      ),
      switchMap(() =>
        this._authenticationFetchService.authenticatorKey().pipe(
          tap({
            next: (response) => {
              if (response && response.status) {
                this.patchState((value) => ({
                  ...value,
                  authenticatorKey: {
                    ...value.authenticatorKey,
                    payload: response.payload,
                  },
                }));
              } else {
                this.patchState((value) => ({
                  ...value,
                  authenticatorKey: {
                    ...value.authenticatorKey,
                    error: {
                      code: response.code,
                      message: response.message,
                    },
                  },
                }));
              }
            },
            error: console.error,
          }),
          catchError(() => EMPTY),
          finalize(() =>
            this.patchState((value) => ({
              ...value,
              authenticatorKey: {
                ...value.authenticatorKey,
                event: {
                  loading: false,
                },
              },
            }))
          )
        )
      )
    )
  );

  public readonly authenticatorCode = this.effect(
    (request$: Observable<AuthenticatorCodeRequestModel>) => {
      return request$.pipe(
        tap(() =>
          this.patchState((value) => ({
            ...value,
            authenticatorCode: {
              event: {
                processing: true,
              },
            },
          }))
        ),
        exhaustMap((request) =>
          this._authenticationFetchService.authenticatorCode(request).pipe(
            tap({
              next: (response) => {
                if (response && response.status) {
                  this.patchState((value) => ({
                    ...value,
                    authenticatorCode: {
                      ...value.authenticatorCode,
                      payload: response.payload,
                    },
                  }));
                } else {
                  this.patchState((value) => ({
                    ...value,
                    authenticatorCode: {
                      ...value.authenticatorCode,
                      error: {
                        code: response.code,
                        message: response.message,
                        translate: 'ERROR.TWO_FACTOR_INCORRECT',
                      },
                    },
                  }));
                }
              },
              error: console.error,
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this.patchState((value) => ({
                ...value,
                authenticatorCode: {
                  ...value.authenticatorCode,
                  event: {
                    processing: false,
                  },
                },
              }))
            )
          )
        )
      );
    }
  );

  public readonly verify = this.effect(($) =>
    $.pipe(
      tap(() =>
        this.patchState((value) => ({
          ...value,
          verify: {
            event: {
              loading: true,
            },
          },
        }))
      ),
      switchMap(() =>
        this._authenticationFetchService.verify().pipe(
          tap({
            next: (response) => {
              if (response && response.status) {
                this.patchState((value) => ({
                  ...value,
                  verify: {
                    ...value.verify,
                    payload: response.payload,
                  },
                }));
              } else {
                this.patchState((value) => ({
                  ...value,
                  verify: {
                    ...value.verify,
                    error: {
                      code: response.code,
                      message: response.message,
                    },
                  },
                }));
              }
            },
            error: console.error,
          }),
          catchError(() => EMPTY),
          finalize(() =>
            this.patchState((value) => ({
              ...value,
              verify: {
                ...value.verify,
                event: {
                  loading: false,
                },
              },
            }))
          )
        )
      )
    )
  );

  public readonly token = this.effect(($) =>
    $.pipe(
      tap(() =>
        this.patchState((value) => ({
          ...value,
          token: {
            event: {
              loading: true,
            },
          },
        }))
      ),
      switchMap(() =>
        this._authenticationFetchService.token().pipe(
          tap({
            next: (response) => {
              if (response && response.status) {
                this.patchState((value) => ({
                  ...value,
                  token: {
                    ...value.token,
                    payload: response.payload,
                  },
                }));
              } else {
                this.patchState((value) => ({
                  ...value,
                  token: {
                    ...value.token,
                    error: {
                      code: response.code,
                      message: response.message,
                    },
                  },
                }));
              }
            },
            error: console.error,
          }),
          catchError(() => EMPTY),
          finalize(() =>
            this.patchState((value) => ({
              ...value,
              token: {
                ...value.token,
                event: {
                  loading: false,
                },
              },
            }))
          )
        )
      )
    )
  );

  public readonly permission = this.effect(($) =>
    $.pipe(
      tap(() =>
        this.patchState((value) => ({
          ...value,
          permission: {
            event: {
              loading: true,
            },
          },
        }))
      ),
      switchMap(() =>
        this._authenticationFetchService.permission().pipe(
          tap({
            next: (response) => {
              if (response && response.status) {
                this.patchState((value) => ({
                  ...value,
                  permission: {
                    ...value.permission,
                    payload: response.payload,
                  },
                }));
              } else {
                this.patchState((value) => ({
                  ...value,
                  permission: {
                    ...value.permission,
                    error: {
                      code: response.code,
                      message: response.message,
                    },
                  },
                }));
              }
            },
            error: console.error,
          }),
          catchError(() => EMPTY),
          finalize(() =>
            this.patchState((value) => ({
              ...value,
              permission: {
                ...value.permission,
                event: {
                  loading: false,
                },
              },
            }))
          )
        )
      )
    )
  );

  public readonly forgotPassword = this.effect(
    (request$: Observable<ForgotPasswordRequestModel>) => {
      return request$.pipe(
        tap(() =>
          this.patchState((value) => ({
            ...value,
            forgotPassword: {
              event: {
                processing: true,
              },
            },
          }))
        ),
        exhaustMap((request) =>
          this._authenticationFetchService.forgotPassword(request).pipe(
            tap({
              next: (response) => {
                if (response && response.status) {
                  this.patchState((value) => ({
                    ...value,
                    forgotPassword: {
                      ...value.forgotPassword,
                      payload: response.payload,
                    },
                  }));
                } else {
                  this.patchState((value) => ({
                    ...value,
                    forgotPassword: {
                      ...value.forgotPassword,
                      error: {
                        code: response.code,
                        message: response.message,
                        translate: 'ERROR.DATA_INCORRECT',
                      },
                    },
                  }));
                }
              },
              error: console.error,
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this.patchState((value) => ({
                ...value,
                forgotPassword: {
                  ...value.forgotPassword,
                  event: {
                    processing: false,
                  },
                },
              }))
            )
          )
        )
      );
    }
  );

  public readonly resetPassword = this.effect(
    (request$: Observable<ResetPasswordRequestModel>) => {
      return request$.pipe(
        tap(() =>
          this.patchState((value) => ({
            ...value,
            resetPassword: {
              event: {
                processing: true,
              },
            },
          }))
        ),
        exhaustMap((request) =>
          this._authenticationFetchService.resetPassword(request).pipe(
            tap({
              next: (response) => {
                if (response && response.status) {
                  this.patchState((value) => ({
                    ...value,
                    resetPassword: {
                      ...value.resetPassword,
                      payload: response.payload,
                    },
                  }));
                } else {
                  this.patchState((value) => ({
                    ...value,
                    resetPassword: {
                      ...value.resetPassword,
                      error: {
                        code: response.code,
                        message: response.message,
                        translate: 'ERROR.DATA_INCORRECT',
                      },
                    },
                  }));
                }
              },
              error: console.error,
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this.patchState((value) => ({
                ...value,
                resetPassword: {
                  ...value.resetPassword,
                  event: {
                    processing: false,
                  },
                },
              }))
            )
          )
        )
      );
    }
  );

  public readonly verifyAuthenticator = this.effect(
    (request$: Observable<VerifyAuthenticatorRequestModel>) => {
      return request$.pipe(
        tap(() =>
          this.patchState((value) => ({
            ...value,
            verifyAuthenticator: {
              event: {
                processing: true,
              },
            },
          }))
        ),
        exhaustMap((request) =>
          this._authenticationFetchService.verifyAuthenticator(request).pipe(
            tap({
              next: (response) => {
                if (response && response.status) {
                  this.patchState((value) => ({
                    ...value,
                    verifyAuthenticator: {
                      ...value.verifyAuthenticator,
                      payload: response.payload,
                    },
                  }));
                } else {
                  this.patchState((value) => ({
                    ...value,
                    verifyAuthenticator: {
                      ...value.verifyAuthenticator,
                      error: {
                        code: response.code,
                        message: response.message,
                      },
                    },
                  }));
                }
              },
              error: console.error,
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this.patchState((value) => ({
                ...value,
                verifyAuthenticator: {
                  ...value.verifyAuthenticator,
                  event: {
                    processing: false,
                  },
                },
              }))
            )
          )
        )
      );
    }
  );

  public readonly activateAuthenticator = this.effect(
    (request$: Observable<ActivateAuthenticatorRequestModel>) => {
      return request$.pipe(
        tap(() =>
          this.patchState((value) => ({
            ...value,
            activateAuthenticator: {
              event: {
                processing: true,
              },
            },
          }))
        ),
        exhaustMap((request) =>
          this._authenticationFetchService.activateAuthenticator(request).pipe(
            tap({
              next: (response) => {
                if (response && response.status) {
                  this.patchState((value) => ({
                    ...value,
                    activateAuthenticator: {
                      ...value.activateAuthenticator,
                      payload: response.payload,
                    },
                  }));
                } else {
                  this.patchState((value) => ({
                    ...value,
                    activateAuthenticator: {
                      ...value.activateAuthenticator,
                      error: {
                        code: response.code,
                        message: response.message,
                      },
                    },
                  }));
                }
              },
              error: console.error,
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this.patchState((value) => ({
                ...value,
                activateAuthenticator: {
                  ...value.activateAuthenticator,
                  event: {
                    processing: false,
                  },
                },
              }))
            )
          )
        )
      );
    }
  );

  public readonly signOut = this.effect(($) =>
    $.pipe(
      tap(() =>
        this.patchState((value) => ({
          ...value,
          signOut: {
            event: {
              processing: true,
            },
          },
        }))
      ),
      exhaustMap(() =>
        this._authenticationFetchService.signOut().pipe(
          tap({
            next: (response) => {
              if (response && response.status) {
                this.patchState((value) => ({
                  ...value,
                  signOut: {
                    ...value.signOut,
                    payload: response.payload,
                  },
                }));
              } else {
                this.patchState((value) => ({
                  ...value,
                  signOut: {
                    ...value.signOut,
                    error: {
                      code: response.code,
                      message: response.message,
                    },
                  },
                }));
              }
            },
            error: console.error,
          }),
          catchError(() => EMPTY),
          finalize(() =>
            this.patchState((value) => ({
              ...value,
              signOut: {
                ...value.signOut,
                event: {
                  processing: false,
                },
              },
            }))
          )
        )
      )
    )
  );

  public resetState(): void {
    this.setState(initialState);
  }
}
